import React, { useCallback, useEffect } from "react";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { PresentationWithId } from "../../../interfaces/IPresentations";
/**
 * ============================================================================
 * SOLO PRESENTATIONS MANAGER - MENEDŻER PREZENTACJI SOLO
 * ============================================================================
 *
 * CEL HOOKA:
 * - Zarządza operacjami wsadowymi dla prezentacji SOLO (jeden zawodnik = jedna prezentacja)
 * - Obsługuje dodawanie, usuwanie i modyfikację wielu prezentacji w jednej operacji
 * - Zapewnia spójność danych i poprawność obliczeń kwot
 * - Ujednolica logikę operacji SOLO w jednym miejscu
 *
 * ARCHITEKTURA:
 * - Custom Hook Pattern: Enkapsuluje logikę zarządzania prezentacjami SOLO
 * - Batch Operations: Wykonuje wszystkie operacje w jednej transakcji
 * - State Management: Zarządza stanem wybranych zawodników
 * - Error Handling: Obsługuje błędy bez blokowania interfejsu
 *
 * PRZEPŁYW DANYCH:
 * 1. Inicjalizuje wybranych zawodników na podstawie istniejących prezentacji
 * 2. Analizuje różnice między stanem aktualnym a docelowym
 * 3. Przygotowuje dane do operacji wsadowych
 * 4. Wykonuje operacje w bazie danych
 * 5. Zamyka formularz po pomyślnym wykonaniu
 *
 * SPECYFIKA SOLO:
 * - Każdy zawodnik = osobna prezentacja z automatyczną nazwą
 * - Operacje wsadowe - wszystkie zmiany w jednej transakcji
 * - Brak tytułu choreografii (SOLO nie wymaga)
 * - Automatyczne numerowanie startowe
 */


type UseSoloPresentationsManagerParams = {
  // Kontekst i dane wejściowe
  initialData?: PresentationWithId;
  applicationId?: string;
  contestId?: string;
  category?: CategoryWithId | null;
  userId?: string;
  existingPresentations: PresentationWithId[];

  // Stan i handlery z formularza
  selectedParticipants: ParticipantWithId[];
  onParticipantsChange: (participants: ParticipantWithId[]) => void;

  // Akcje zapisu/usuwania
  handlePresentationAdd: (presentationData: PresentationWithId) => Promise<any>;
  handlePresentationDelete: (presentationId: string) => Promise<any>;
  handleSoloPresentationsBatch: (
    presentationsToAdd: PresentationWithId[],
    presentationsToDelete: PresentationWithId[]
  ) => Promise<any>;

  // UI
  onCancel: () => void;

  // Test mode – blokuje zapis do bazy i tylko loguje
  testMode?: boolean;
};

/**
 * ============================================================================
 * HOOK: useSoloPresentationsManager
 * ============================================================================
 * 
 * GŁÓWNA FUNKCJA HOOKA:
 * - Zarządza operacjami wsadowymi dla prezentacji SOLO
 * - Obsługuje inicjalizację, analizę różnic i wykonanie operacji
 * - Zwraca funkcję handleSaveSolo do użycia w formularzu
 * 
 * PARAMETRY WEJŚCIOWE:
 * - initialData: Dane początkowe prezentacji (dla trybu edycji)
 * - applicationId, contestId, categoryId: Identyfikatory kontekstu
 * - userId: ID użytkownika wykonującego operację
 * - existingPresentations: Lista istniejących prezentacji w kategorii
 * - selectedParticipants: Aktualnie wybrani zawodnicy w formularzu
 * - onParticipantsChange: Handler zmiany wyboru zawodników
 * - handleSoloPresentationsBatch: Funkcja wykonująca operacje wsadowe
 * - onCancel: Handler zamknięcia formularza
 * - testMode: Tryb testowy (blokuje zapis do bazy)
 * 
 * ZWRACA:
 * - handleSaveSolo: Funkcja do zapisywania zmian w prezentacjach SOLO
 */
export function useSoloPresentationsManager({
  initialData,
  applicationId,
  contestId,
  category,
  userId,
  existingPresentations,
  selectedParticipants,
  onParticipantsChange,
  handlePresentationAdd,
  handlePresentationDelete,
  handleSoloPresentationsBatch,
  onCancel,
  testMode = false,
}: UseSoloPresentationsManagerParams) {
  // Inicjalizacja wybranych zawodników na bazie istniejących prezentacji
  useEffect(() => {
    if (!initialData && existingPresentations.length > 0) {
      const existingSoloParticipants = existingPresentations
        .map((p) => p.participants?.[0])
        .filter((participant): participant is ParticipantWithId => !!participant);
      if (existingSoloParticipants.length > 0) {
        onParticipantsChange(existingSoloParticipants);
      }
    }
  }, [initialData, existingPresentations, onParticipantsChange]);

  /**
   * ============================================================================
   * FUNKCJA: handleSaveSolo
   * ============================================================================
   * 
   * CEL FUNKCJI:
   * - Wykonuje operacje wsadowe na prezentacjach SOLO
   * - Analizuje różnice między stanem aktualnym a docelowym
   * - Wykonuje operacje dodawania i usuwania w jednej transakcji
   * - Zapewnia spójność danych i poprawność obliczeń kwot
   * 
   * ALGORYTM:
   * 1. Analiza różnic - porównuje istniejące prezentacje z wybranymi zawodnikami
   * 2. Określenie operacji - co dodać, co usunąć, co zostawić bez zmian
   * 3. Przygotowanie danych - tworzy obiekty prezentacji do dodania
   * 4. Wykonanie operacji - wywołuje funkcję wsadową
   * 5. Zamknięcie formularza - wraca do listy po pomyślnym wykonaniu
   * 
   * SPECYFIKA SOLO:
   * - Każdy zawodnik = osobna prezentacja
   * - Automatyczna nazwa prezentacji (imię + nazwisko)
   * - Brak tytułu choreografii
   * - Operacje wsadowe - wszystkie zmiany w jednej transakcji
   */
  const handleSaveSolo = useCallback(async () => {
    // ============================================================================
    // ANALIZA RÓŻNIC W STANIE PREZENTACJI
    // ============================================================================
    // Porównuje aktualny stan wybranych zawodników z istniejącymi prezentacjami
    // w celu określenia operacji do wykonania (dodanie, usunięcie, bez zmian)
    
    // Zbiór ID zawodników w istniejących prezentacjach (po wejściu do formularza)
    const existingIds = new Set(
      existingPresentations.map((p) => p.participants?.[0]?.id).filter((id): id is string => !!id)
    );
    const currentIds = new Set(selectedParticipants.map((p) => p.id));

    // Do usunięcia: prezentacje, których zawodnik nie jest już wybrany
    const toDelete = existingPresentations.filter((p) => {
      const pid = p.participants?.[0]?.id;
      return pid && !currentIds.has(pid);
    });

    // Do dodania: zawodnicy wybrani teraz, których nie było w existing
    const toAdd = selectedParticipants.filter(
      (p): p is ParticipantWithId => !!p.id && !existingIds.has(p.id)
    );

    // Bez zmian: przecięcie existingIds ∩ currentIds
    const unchangedIds = Array.from(existingIds).filter((id) => currentIds.has(id));

    // ============================================================================
    // ANALIZA RÓŻNIC W STANIE PREZENTACJI SOLO
    // ============================================================================
    // Porównuje aktualny stan wybranych zawodników z istniejącymi prezentacjami
    // w celu określenia operacji do wykonania (dodanie, usunięcie, bez zmian)
    // Wejście: existingPresentations (prezentacje w bazie), selectedParticipants (wybrani zawodnicy)
    // Wyjście: toDelete (do usunięcia), toAdd (do dodania), unchangedIds (bez zmian)

    // ============================================================================
    // WALIDACJA TRYBU TESTOWEGO
    // ============================================================================
    // W trybie testowym operacje są blokowane - tylko logowanie różnic
    // Używane do debugowania bez wpływu na bazę danych
    if (testMode) {
      return;
    }

    // ============================================================================
    // PRZYGOTOWANIE DANYCH DO OPERACJI WSADOWYCH
    // ============================================================================
    // Tworzy obiekty prezentacji dla zawodników do dodania
    // Każdy zawodnik SOLO = jedna prezentacja z automatyczną nazwą
    const presentationsToAdd: PresentationWithId[] = toAdd.map((participant) => ({
      id: "", // Pusty ID - zostanie wygenerowany przez bazę danych
      name: `${participant.firstName} ${participant.lastName}`, // Automatyczna nazwa
      title: "", // SOLO nie wymaga tytułu choreografii
      participants: [participant], // Jeden zawodnik na prezentację
      applicationId: applicationId || "",
      contestId: contestId || "",
      categoryId: category?.id || "",
      userId: userId || "",
      startNo: 0, // Numer startowy zostanie przypisany później
    }));

    // ============================================================================
    // WYKONANIE OPERACJI WSADOWYCH
    // ============================================================================
    // Wykonuje wszystkie operacje (dodawanie i usuwanie) w jednej transakcji
    // Zapewnia spójność danych i poprawność obliczeń kwot
    try {
      const result = await handleSoloPresentationsBatch(presentationsToAdd, toDelete);
      
      if (!result.success) {
        throw new Error(result.error || "Nieznany błąd podczas operacji wsadowych");
      }
    } catch (error) {
      // Błąd jest już zalogowany w handleSoloPresentationsBatch
      // Nie rzucamy dalej - pozwalamy na zamknięcie formularza
    }

    // ============================================================================
    // ZAMKNIĘCIE FORMULARZA
    // ============================================================================
    // Po pomyślnym wykonaniu operacji zamyka formularz i wraca do listy
    onCancel();
  }, [
    applicationId,
    contestId,
    category?.id,
    existingPresentations,
    selectedParticipants,
    handlePresentationAdd,
    handlePresentationDelete,
    handleSoloPresentationsBatch,
    onCancel,
    testMode,
    userId,
  ]);

  return {
    handleSaveSolo,
  };
}
